import { ReportRegenerationDetailedResponse, ReportRegenerationOutcome, ReportRegenerationResult } from 'types/ModelTypes';

const outcomes: ReadonlySet<string> = new Set(Object.values(ReportRegenerationOutcome));

export const isReportRegenerationDetailedResponse = (
    data: unknown,
    requestedIds: readonly number[],
): data is ReportRegenerationDetailedResponse => {
    if (!data || typeof data !== 'object' || !('results' in data) || !Array.isArray(data.results)) return false;
    const expected = new Set(requestedIds);
    if (expected.size === 0 || requestedIds.some((id) => !Number.isSafeInteger(id) || id <= 0) || data.results.length !== expected.size) return false;

    const seen = new Set<number>();
    return data.results.every((result: unknown) => {
        if (!result || typeof result !== 'object' || !('reportStatusId' in result) || !('outcome' in result)) return false;
        const id = result.reportStatusId;
        if (typeof id !== 'number' || !Number.isSafeInteger(id) || id <= 0 || !expected.has(id) || seen.has(id)) return false;
        if (typeof result.outcome !== 'string' || !outcomes.has(result.outcome)) return false;
        if ('reasonCode' in result && result.reasonCode != null && typeof result.reasonCode !== 'string') return false;
        if (result.outcome !== ReportRegenerationOutcome.Unavailable && 'reasonCode' in result && result.reasonCode != null) return false;
        seen.add(id);
        return true;
    });
};

export const getRegenerationUnavailableMessage = (reasonCode?: string | null): string => {
    switch (reasonCode) {
        case 'notReady':
            return 'This report cannot be regenerated until the underlying data is corrected.';
        case 'invalidRequest':
            return 'This report could not be found. Refresh the report list and try again.';
        case 'batchRejected':
            return 'No reports were changed because another selected report could not be found. Refresh the report list and try again.';
        case 'unsupportedReportType':
            return 'Regeneration is not supported for this report type.';
        case 'missingVessel':
            return 'This report cannot be regenerated because vessel information is unavailable.';
        case 'noReportContent':
            return 'The current data contains no eligible report content.';
        default:
            return 'This report is currently unavailable for regeneration.';
    }
};

export const buildRegenerationSummary = (
    results: readonly ReportRegenerationResult[],
): { severity: 'success' | 'info' | 'warning'; text: string } => {
    const regenerated = results.filter((result) => result.outcome === ReportRegenerationOutcome.Regenerated).length;
    const upToDate = results.filter((result) => result.outcome === ReportRegenerationOutcome.UpToDate).length;
    const unavailable = results.length - regenerated - upToDate;
    if (results.length === 1) {
        if (regenerated) return { severity: 'success', text: 'The report has been updated. No email was sent.' };
        if (upToDate) return { severity: 'info', text: 'This report is already up to date. No regeneration is needed.' };
        return { severity: 'warning', text: getRegenerationUnavailableMessage(results[0].reasonCode) };
    }
    const parts: string[] = [];
    if (regenerated) parts.push(`${regenerated} report${regenerated === 1 ? '' : 's'} updated`);
    if (upToDate) parts.push(`${upToDate} already up to date`);
    if (unavailable) parts.push(`${unavailable} could not be regenerated`);
    return {
        severity: unavailable ? 'warning' : regenerated ? 'success' : 'info',
        text: `${parts.join('; ')}. No email was sent.`,
    };
};

export const getSuccessfullyHandledReportIds = (results: readonly ReportRegenerationResult[]): Set<number> =>
    new Set(
        results
            .filter((result) => result.outcome === ReportRegenerationOutcome.Regenerated || result.outcome === ReportRegenerationOutcome.UpToDate)
            .map((result) => result.reportStatusId),
    );
